import React, { ReactElement } from 'react';
import { useLocation, Outlet } from 'react-router-dom';
import { Show } from 'framework/show';
import { ShowPageLayout } from 'framework/ui/layout';
import EmployeeEntityHeader from './EmployeeEntityHeader';
import EmployeeCard from '../EmployeeCard/EmployeeCard';
import { useEmployeeId } from 'domain/people/employee/employeeIdentityRouting';
import useEmployeesDashboardService from '../dashboard/useEmployeesDashboardService';
import { READ_DELEGATED_DASHBOARD_ENTRIES } from 'domain/people/employee/dashboard/constants';
import FinalizeEmployeeButton from './button/FinalizeEmployeeButton';
import DeleteEmployeeButton from './button/DeleteEmployeeButton';
import EnableEmailScanningButton from '../mailboxConsent/shared/EnableEmailScanningButton';
import DisableEmailScanningButton from '../mailboxConsent/shared/DisableEmailScanningButton';
import useEmployeeContractsService from '../contracts/shared/employeeContractsService';
import SingleEmployeeBreadcrumbs from './SingleEmployeeBreadcrumbs';
import { useAuthorization } from 'framework/authorization';
import { Box } from '@mui/material';
import { styled } from '@mui/system';
import EmployeeDashboardModalsProvider from '../dashboard/components/EmployeeDashboardModalsProvider';
import { TimelineRefreshProvider } from '../dashboard/List/EmployeeDashboardItem/TimelineRefreshContext';
import {
  useIsMyProfile,
  useEmployeeService,
  READ_EMPLOYEE_CONTRACTS,
  READ_EMPLOYEE_DASHBOARD,
} from 'domain/shared/employee';
import useEmployeePositionSlotService from '../CareerCapabilities/positionSlot/useEmployeePositionSlotService';

const StickyBox = styled(Box)(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    position: 'sticky',
    top: '64px',
    zIndex: 11,
  },
}));

/**
 * A wrapper component that provides a stable container for the EmployeeLayout
 * This prevents the entire layout from remounting when navigating between pages
 */
const EmployeeLayoutWrapper: React.FC = () => {
  const location = useLocation();
  const employeeId = useEmployeeId();
  const { getById } = useEmployeeService();
  const isMyProfile = useIsMyProfile();
  const { isGranted } = useAuthorization();
  const { getAllEmployessForDashboard, getCombinedEmployeesForDashboard } = useEmployeesDashboardService();
  const { getAll } = useEmployeeContractsService();
  const { getEmployeePositionSlotId } = useEmployeePositionSlotService();

  const fetchCombinedData = async () => {
    const loadParams = {
      pageSize: 100,
      pageNumber: 0,
      filter: {
        searchString: {
          ct: employeeId,
        },
      },
    };

    const fetchEmployeeContractsAccordingToPermissions = async () => {
      if (isMyProfile || !isGranted(READ_EMPLOYEE_CONTRACTS)) {
        return { result: [] };
      }

      try {
        return await getAll(employeeId);
      } catch (error) {
        if ((error as Response).status === 403) {
          return { result: [] };
        }
        throw error;
      }
    };

    const fetchEmployeesDashboardAccordingToPermissions = async () => {
      if (isGranted(READ_DELEGATED_DASHBOARD_ENTRIES)) {
        return await getCombinedEmployeesForDashboard(loadParams);
      }

      return await getAllEmployessForDashboard(loadParams);
    };

    const [employeeDetails, contracts, dashboardData, positionSlotId] = await Promise.all([
      getById(employeeId!),
      fetchEmployeeContractsAccordingToPermissions(),
      isMyProfile || !isGranted(READ_EMPLOYEE_DASHBOARD)
        ? Promise.resolve({ result: [] })
        : fetchEmployeesDashboardAccordingToPermissions(),
      getEmployeePositionSlotId(employeeId!),
    ]);

    // The dashboard search matches the alias as a substring of any search string, so pick this employee's row.
    const dashboardRecord = dashboardData.result.find(
      (record: { employeeAlias?: string }) => record.employeeAlias === employeeId,
    );

    return {
      ...employeeDetails,
      сontracts: contracts?.result || [],
      positionSlotId,
      retentionRiskAssigneeAlias: dashboardRecord?.retentionRiskAssigneeAlias,
      retentionRiskFollowUps: dashboardRecord?.retentionRiskFollowUps,
      retentionRiskId: dashboardRecord?.retentionRiskId,
      retentionRiskIssues: dashboardRecord?.retentionRiskIssues,
      retentionRiskLastHealthcheck: dashboardRecord?.retentionRiskLastHealthcheck,
      retentionRiskPriority: dashboardRecord?.retentionRiskPriority,
      hasFullRetentionRiskAccessAccordingToSpecificManagers:
        dashboardRecord?.hasFullRetentionRiskAccessAccordingToSpecificManagers,
    };
  };

  const getHeaderButtons = (): ReactElement[] => {
    const isPersonalLegalView = location.pathname.endsWith('/personal-legal');

    if (isPersonalLegalView && !isMyProfile) {
      return [
        <FinalizeEmployeeButton key="finalize" />,
        <DeleteEmployeeButton key="delete" />,
        <EnableEmailScanningButton key="enable-email" />,
        <DisableEmailScanningButton key="disable-email" />,
      ];
    }

    return [];
  };

  return (
    <Show getDataMethod={fetchCombinedData}>
      <TimelineRefreshProvider>
        <EmployeeDashboardModalsProvider>
          <ShowPageLayout
            breadcrumbs={!isMyProfile ? <SingleEmployeeBreadcrumbs /> : undefined}
            entityHeader={<EmployeeEntityHeader />}
            headerButtons={getHeaderButtons()}
            hasSeparateSections
          >
            <StickyBox>
              <EmployeeCard />
            </StickyBox>
            <Outlet />
          </ShowPageLayout>
        </EmployeeDashboardModalsProvider>
      </TimelineRefreshProvider>
    </Show>
  );
};

export default EmployeeLayoutWrapper;
